import React, { useEffect, useState } from "react";
import { Card, Form, Button } from "react-bootstrap";
import {
  getUserProfile,
  updateUserProfile,
  useUser,
  useAppDispatch,
} from "../../redux";
import { countries } from "../../utils/countries";

const MyAccountTab = () => {
  const dispatch = useAppDispatch();
  const { profile, loading } = useUser();
  const [form, setForm] = useState({
    first_name: "",
    last_name: "",
    mobile: "",
    email: "",
    company_name: "",
    gst_number: "",
    vat_number: "",
    billing_address: "",
    additional_address: "",
    zip_code: "",
    city: "",
    state: "",
    country: "",
  });
  const [captcha, setCaptcha] = useState(false);

  // Dispatch action on component mount
  useEffect(() => {
    dispatch(getUserProfile());
  }, [dispatch]);

  // Handle form initialization when profile data is available
  useEffect(() => {
    if (profile) {
      setForm({
        first_name: profile.first_name || "",
        last_name: profile.last_name || "",
        mobile: profile.mobile || "",
        email: profile.email || "",
        company_name: profile.company_name || "",
        gst_number: profile.gst_number || "",
        vat_number: profile.vat_number || "",
        billing_address: profile.billing_address || "",
        additional_address: profile.additional_address || "",
        zip_code: profile.zip_code || "",
        city: profile.city || "",
        state: profile.state || "",
        country: profile.country || "",
      });
    }
  }, [profile]);

  const handleChange = (e) => {
    setForm({ ...form, [e.target.name]: e.target.value });
  };

  const handleSubmit = (e) => {
    e.preventDefault();

    // Filter out empty fields except email (which is required)
    const filteredForm = Object.keys(form).reduce((acc, key) => {
      if (key === "email" || (form[key] && form[key].trim() !== "")) {
        acc[key] = form[key];
      }
      return acc;
    }, {});

    dispatch(updateUserProfile(filteredForm));
  };

  if (loading && !profile) {
    return <div>Loading...</div>;
  }

  return (
    <div className="my-account-tab p-3">
      <div className="d-flex justify-content-center">
        <Card style={{ width: "100%" }} className="shadow-sm">
          <Card.Header className="bg-primary text-white text-center py-3">
            <h4 className="mb-0 fw-bold">My Account Information</h4>
          </Card.Header>
          <Card.Body className="p-4">
            <Form onSubmit={handleSubmit}>
              {/* Personal Information Section */}
              <div className="mb-3">
                <h6 className="text-primary fw-bold mb-2 border-bottom pb-1">
                  Personal Information
                </h6>
                <div className="row g-2">
                  <div className="col-md-6">
                    <Form.Group controlId="first_name">
                      <Form.Label className="small fw-semibold">
                        First Name
                      </Form.Label>
                      <Form.Control
                        name="first_name"
                        value={form.first_name}
                        onChange={handleChange}
                        size="sm"
                        className="border-0 bg-light"
                      />
                    </Form.Group>
                  </div>
                  <div className="col-md-6">
                    <Form.Group controlId="last_name">
                      <Form.Label className="small fw-semibold">
                        Last Name
                      </Form.Label>
                      <Form.Control
                        name="last_name"
                        value={form.last_name}
                        onChange={handleChange}
                        size="sm"
                        className="border-0 bg-light"
                      />
                    </Form.Group>
                  </div>
                </div>
                <div className="row g-2 mt-1">
                  <div className="col-md-6">
                    <Form.Group controlId="mobile">
                      <Form.Label className="small fw-semibold">
                        Mobile
                      </Form.Label>
                      <Form.Control
                        name="mobile"
                        value={form.mobile}
                        onChange={handleChange}
                        size="sm"
                        className="border-0 bg-light"
                      />
                    </Form.Group>
                  </div>
                  <div className="col-md-6">
                    <Form.Group controlId="email">
                      <Form.Label className="small fw-semibold">
                        Email <span className="text-danger">*</span>
                      </Form.Label>
                      <Form.Control
                        name="email"
                        value={form.email}
                        readOnly
                        size="sm"
                        className="border-0 bg-light"
                        style={{
                          backgroundColor: "#f8f9fa",
                          cursor: "not-allowed",
                        }}
                      />
                      <Form.Text className="text-muted">
                        Email cannot be changed
                      </Form.Text>
                    </Form.Group>
                  </div>
                </div>
              </div>

              {/* Company Information Section */}
              <div className="mb-3">
                <h6 className="text-primary fw-bold mb-2 border-bottom pb-1">
                  Company Information
                </h6>
                <div className="row g-2">
                  <div className="col-md-4">
                    <Form.Group controlId="company_name">
                      <Form.Label className="small fw-semibold">
                        Company Name
                      </Form.Label>
                      <Form.Control
                        name="company_name"
                        value={form.company_name}
                        onChange={handleChange}
                        size="sm"
                        className="border-0 bg-light"
                      />
                    </Form.Group>
                  </div>
                  <div className="col-md-4">
                    <Form.Group controlId="gst_number">
                      <Form.Label className="small fw-semibold">
                        GST Number
                      </Form.Label>
                      <Form.Control
                        name="gst_number"
                        value={form.gst_number}
                        onChange={handleChange}
                        size="sm"
                        className="border-0 bg-light"
                      />
                    </Form.Group>
                  </div>
                  <div className="col-md-4">
                    <Form.Group controlId="vat_number">
                      <Form.Label className="small fw-semibold">
                        VAT Number
                      </Form.Label>
                      <Form.Control
                        name="vat_number"
                        value={form.vat_number}
                        onChange={handleChange}
                        size="sm"
                        className="border-0 bg-light"
                      />
                    </Form.Group>
                  </div>
                </div>
              </div>

              {/* Address Information Section */}
              <div className="mb-3">
                <h6 className="text-primary fw-bold mb-2 border-bottom pb-1">
                  Address Information
                </h6>
                <div className="row g-2">
                  <div className="col-md-6">
                    <Form.Group controlId="billing_address">
                      <Form.Label className="small fw-semibold">
                        Billing Address
                      </Form.Label>
                      <Form.Control
                        name="billing_address"
                        value={form.billing_address}
                        onChange={handleChange}
                        size="sm"
                        className="border-0 bg-light"
                      />
                    </Form.Group>
                  </div>
                  <div className="col-md-6">
                    <Form.Group controlId="additional_address">
                      <Form.Label className="small fw-semibold">
                        Additional Address
                      </Form.Label>
                      <Form.Control
                        name="additional_address"
                        value={form.additional_address}
                        onChange={handleChange}
                        size="sm"
                        className="border-0 bg-light"
                      />
                    </Form.Group>
                  </div>
                </div>
                <div className="row g-2 mt-1">
                  <div className="col-md-3">
                    <Form.Group controlId="zip_code">
                      <Form.Label className="small fw-semibold">
                        Zip Code
                      </Form.Label>
                      <Form.Control
                        name="zip_code"
                        value={form.zip_code}
                        onChange={handleChange}
                        size="sm"
                        className="border-0 bg-light"
                      />
                    </Form.Group>
                  </div>
                  <div className="col-md-3">
                    <Form.Group controlId="city">
                      <Form.Label className="small fw-semibold">
                        City
                      </Form.Label>
                      <Form.Control
                        name="city"
                        value={form.city}
                        onChange={handleChange}
                        size="sm"
                        className="border-0 bg-light"
                      />
                    </Form.Group>
                  </div>
                  <div className="col-md-3">
                    <Form.Group controlId="state">
                      <Form.Label className="small fw-semibold">
                        State
                      </Form.Label>
                      <Form.Control
                        name="state"
                        value={form.state}
                        onChange={handleChange}
                        size="sm"
                        className="border-0 bg-light"
                      />
                    </Form.Group>
                  </div>
                  <div className="col-md-3">
                    <Form.Group controlId="country">
                      <Form.Label className="small fw-semibold">
                        Country
                      </Form.Label>
                      <Form.Select
                        name="country"
                        value={form.country}
                        onChange={handleChange}
                        size="sm"
                        className="border-0 bg-light"
                      >
                        <option value="">Select Country</option>
                        {countries.map((country) => (
                          <option key={country.code} value={country.name}>
                            {country.name}
                          </option>
                        ))}
                      </Form.Select>
                    </Form.Group>
                  </div>
                </div>
              </div>

              {/* Captcha and Submit Section */}
              <div className="d-flex justify-content-between align-items-center mt-4 pt-3 border-top">
                <div className="d-flex align-items-center">
                  <div className="border rounded p-2 bg-light me-2">
                    <Form.Check
                      type="checkbox"
                      id="captchaCheck"
                      label="I'm not a robot"
                      checked={captcha}
                      onChange={(e) => setCaptcha(e.target.checked)}
                      required
                      className="mb-0"
                    />
                  </div>
                  <span className="text-muted small">reCAPTCHA</span>
                </div>

                <Button
                  type="submit"
                  variant="primary"
                  size="md"
                  className="px-4 fw-bold"
                  disabled={!captcha || loading}
                >
                  {loading ? "Updating..." : "Update Profile"}
                </Button>
              </div>
            </Form>
          </Card.Body>
        </Card>
      </div>
    </div>
  );
};

export default MyAccountTab;
